import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CircularProgress,
  Alert,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Button,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  OpenInNew as OpenInNewIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import { type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid';
import { DataGrid, PageTitle, ListLayoutPanel, GridEditButton, MainSectionPanel } from '../../../components/common';
import { useParteProduccionStore } from './store/useParteProduccionStore';

type EstadoParte = 'PEN' | 'APR' | 'ANU';

const estadoLabels: Record<EstadoParte, string> = {
  'PEN': 'Pendiente',
  'APR': 'Aprobado',
  'ANU': 'Anulado',
};

const estadoColors: Record<EstadoParte, 'warning' | 'success' | 'error'> = {
  'PEN': 'warning',
  'APR': 'success',
  'ANU': 'error',
};

const ParteProduccionPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    partes,
    loading,
    error,
    fetchPartes,
    clearError
  } = useParteProduccionStore();

  const handleNavigateToGuiaProduccion = (idGuiaProduccion: number) => {
    navigate(`/produccion/guias-produccion/${idGuiaProduccion}`);
  };

  const columns: GridColDef[] = [
    {
      field: 'numeroComprobante',
      headerName: 'N° Parte',
      width: 80,
      type: 'number'
    },
    {
      field: 'numeroGuiaProduccion',
      headerName: 'Guía Prod',
      width: 95,
      renderCell: (params: GridRenderCellParams) => {
        const idGuiaProduccion = params.row.idGuiaProduccion;
        const numeroGuia = params.value;

        if (!idGuiaProduccion) return '-';

        return (
          <Tooltip title="Ver Guía de Producción">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'pointer',
                color: 'primary.main',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleNavigateToGuiaProduccion(idGuiaProduccion);
              }}
            >
              {numeroGuia || idGuiaProduccion}
              <OpenInNewIcon sx={{ fontSize: 14 }} />
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: 'numeroDerc',
      headerName: 'N° DERC',
      width: 130,
    },
    {
      field: 'fecha',
      headerName: 'Fecha',
      width: 90,
      type: 'date',
      valueFormatter: (value) => {
        if (!value) return '';
        return new Date(value).toLocaleDateString('es-ES');
      }
    },
    {
      field: 'asociacion',
      headerName: 'Asociación',
      width: 120
    },
    {
      field: 'cliente',
      headerName: 'Cliente',
      width: 180,
      flex: 1,
      minWidth: 150
    },
    {
      field: 'responsable',
      headerName: 'Responsable',
      width: 110
    },
    {
      field: 'numeroGuiaRemision',
      headerName: 'Guía Remisión',
      width: 110
    },
    {
      field: 'cajas',
      headerName: 'Cajas',
      width: 70,
      type: 'number',
      renderCell: (params: GridRenderCellParams) => {
        if (params.value == null) return '';
        return (
          <Chip
            label={params.value}
            size="small"
            color="primary"
            variant="outlined"
          />
        );
      },
    },
    {
      field: 'estado',
      headerName: 'Estado',
      width: 110,
      renderCell: (params: GridRenderCellParams) => {
        const estado = (params.value as EstadoParte) || 'PEN';
        return (
          <Chip
            label={estadoLabels[estado] || estado}
            color={estadoColors[estado] || 'default'}
            size="small"
          />
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 100,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const isPendiente = params.row.estado === 'PEN';

        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {isPendiente ? (
              <GridEditButton
                onClick={() => handleEdit(params.row.id)}
                tooltip="Editar"
                fontSize="small"
                ariaLabel={`Editar parte ${params.row.numeroComprobante}`}
              />
            ) : (
              <Tooltip title="Ver detalle">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => handleView(params.row.id)}
                  aria-label={`Ver parte ${params.row.numeroComprobante}`}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  useEffect(() => {
    fetchPartes();
  }, [fetchPartes]);

  const _handleAdd = () => {
    navigate('/produccion/partes-produccion/new');
  };
  void _handleAdd; // Explicitly mark as intentionally unused

  const handleEdit = (id: number) => {
    navigate(`/produccion/partes-produccion/${id}`);
  };

  const handleView = (id: number) => {
    navigate(`/produccion/partes-produccion/${id}?readonly=true`);
  };

  const handleCloseError = () => {
    clearError();
  };

  const handleGoToGuiasProduccion = () => {
    navigate('/produccion/guias-produccion');
  };

  return (
    <MainSectionPanel>
      <PageTitle
        title="Partes de Producción"
        subtitle="Gestión de Partes de Producción"
        flowStep={3}
        actions={
          <Button
            variant="outlined"
            color="primary"
            startIcon={<AssignmentIcon />}
            onClick={handleGoToGuiasProduccion}
            size="small"
          >
            Guías Producción
          </Button>
        }
      />

      <ListLayoutPanel>

        {error && (
          <Alert
            severity="error"
            onClose={handleCloseError}
            sx={{ mb: 2 }}
          >
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            rows={partes}
            columns={columns}
            loading={loading}
            getRowId={(row) => row.id}
            pageSize={25}
          />
        )}
      </ListLayoutPanel>
    </MainSectionPanel>
  );
};

export default ParteProduccionPage;